import React, { useEffect, useState } from 'react';
import { useTheme } from '../providers/ThemeProvider';
import { useSAPDataStore } from '../store/SAPDataStore';
import { ChartDataService } from '../services/ChartDataService';
import { useUnifiedAuth } from '../auth/useUnifiedAuth';
import { AvailableDatePicker } from './AvailableDatePicker';

interface PresetValuesProps {
  machineId?: string;
  machineData?: {
    serialNumber?: string;
  };
}

interface PresetInfo {
  preset_id: number;
  preset_name: string;
  current_value: number;
  number_changes: number;
  machine_sn?: string;
  shift_name?: string;
  timestamp?: string;
}

// Backend returns flat array of presets
type PresetResponse = PresetInfo[];

interface ComparisonData {
  id: number;
  preset: string;
  leftValue: string | number;
  leftChanges: number;
  rightValue: string | number;
  rightChanges: number;
}

const PresetValues: React.FC<PresetValuesProps> = ({ 
  machineId, 
  machineData
}) => {
  const { currentTheme } = useTheme();
  const { token } = useUnifiedAuth();
  const { dateRange } = useSAPDataStore();
  
  // Available dates and shifts
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [leftShifts, setLeftShifts] = useState<string[]>([]);
  const [rightShifts, setRightShifts] = useState<string[]>([]);
  
  // Selected values
  const [leftDate, setLeftDate] = useState<string>('');
  const [leftShift, setLeftShift] = useState<string>('');
  const [rightDate, setRightDate] = useState<string>('');
  const [rightShift, setRightShift] = useState<string>('');
  
  // Data and UI state
  const [leftData, setLeftData] = useState<PresetResponse | null>(null);
  const [rightData, setRightData] = useState<PresetResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [sortField, setSortField] = useState<string>('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showOnlyDifferences, setShowOnlyDifferences] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Debug: log when data changes
  useEffect(() => {
    console.log('📊 PresetValues: State updated - leftData:', leftData ? (Array.isArray(leftData) ? `array(${leftData.length})` : typeof leftData) : 'null');
    console.log('📊 PresetValues: State updated - rightData:', rightData ? (Array.isArray(rightData) ? `array(${rightData.length})` : typeof rightData) : 'null');
  }, [leftData, rightData]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Function to fetch available dates via NATS
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fetchAvailableDates = async (serialno: string, _dateFrom: string, _dateTo: string): Promise<string[]> => {
    if (!token) return [];
    
    try {
      const params = { serialno };
      const response = await ChartDataService.fetchAvailableDates(params, token);
      
      if (response.success && response.data) {
        // Backend returns array of {date, exist_SAP, machine_sn} - extract just dates
        const results = Array.isArray(response.data) ? response.data : (response.data.available_dates || []);
        return results.map((item: { date: string }) => item.date.split('T')[0]); // Extract date part
      }
      return [];
    } catch (err) {
      console.error('PresetValues: Error fetching available dates:', err);
      return [];
    }
  };

  // Function to fetch available shifts for a specific date
  const fetchAvailableShifts = async (serialno: string, date: string): Promise<string[]> => {
    if (!token) return [];
    
    try {
      const params = { serialno, date_from: date, date_to: date };
      const response = await ChartDataService.fetchAvailableShifts(params, token);
      
      console.log('📊 PresetValues: fetchAvailableShifts response:', response);
      
      // Response format: { success: true, data: [{shift_name, machine_sn, exist_SAP}, ...] }
      // Handle missing data as empty array
      if (response.success && response.data) {
        // Backend may return array directly or nested
        const shifts = Array.isArray(response.data) ? response.data : (response.data.results || []);
        console.log('📊 PresetValues: Extracted shifts:', shifts);
        return shifts.map((item: { shift_name: string }) => item.shift_name);
      }
      return [];
    } catch (err) {
      console.error('PresetValues: Error fetching available shifts:', err);
      return [];
    }
  };

  // Function to fetch preset data for a specific date and shift
  const fetchPresetData = async (serialno: string, date: string, shift: string): Promise<PresetResponse | null> => {
    if (!token) return null;
    
    try {
      const params = ChartDataService.buildRequestParams(serialno, { start: date, end: date }, [shift]);
      const response = await ChartDataService.fetchPresets(params, token);
      console.log('📊 PresetValues: fetchPresetData response:', response);
      
      if (response.success && response.data) {
        // Backend returns flat array of presets
        const presets = Array.isArray(response.data) ? response.data : (response.data.results || []);
        console.log('📊 PresetValues: Extracted presets:', presets.length, 'items');
        return presets as PresetResponse;
      }
      return null;
    } catch (err) {
      console.error('PresetValues: Error fetching data:', err);
      return null;
    }
  };

  // Load available dates when component mounts
  useEffect(() => {
    if (machineId && dateRange.start && dateRange.end) {
      const serialno = machineData?.serialNumber || machineId;
      fetchAvailableDates(serialno, dateRange.start, dateRange.end)
        .then(dates => {
          setAvailableDates(dates);
          // Set oldest date for left side and newest date for right side
          if (dates.length > 0) {
            // Sort dates to ensure we get oldest and newest
            const sortedDates = [...dates].sort();
            setLeftDate(sortedDates[0]); // Oldest date
            setRightDate(sortedDates[sortedDates.length - 1]); // Newest date
          }
        });
    }
  }, [machineId, machineData, dateRange.start, dateRange.end]);

  // Load shifts when left date changes
  useEffect(() => {
    console.log('📊 PresetValues: Left date changed to:', leftDate);
    if (leftDate && machineId) {
      const serialno = machineData?.serialNumber || machineId;
      fetchAvailableShifts(serialno, leftDate)
        .then(shifts => {
          console.log('📊 PresetValues: Left shifts loaded:', shifts);
          setLeftShifts(shifts);
          if (shifts.length > 0) {
            setLeftShift(shifts[0]);
          }
        });
    }
  }, [leftDate, machineId, machineData]);

  // Load shifts when right date changes
  useEffect(() => {
    console.log('📊 PresetValues: Right date changed to:', rightDate);
    if (rightDate && machineId) {
      const serialno = machineData?.serialNumber || machineId;
      fetchAvailableShifts(serialno, rightDate)
        .then(shifts => {
          console.log('📊 PresetValues: Right shifts loaded:', shifts);
          setRightShifts(shifts);
          if (shifts.length > 0) {
            setRightShift(shifts[0]);
          }
        });
    }
  }, [rightDate, machineId, machineData]);

  // Load comparison data when selections change
  useEffect(() => {
    console.log('📊 PresetValues: Selection check:', { leftDate, leftShift, rightDate, rightShift, machineId });
    
    if (leftDate && leftShift && rightDate && rightShift && machineId) {
      setLoading(true);
      setError(null);
      
      const serialno = machineData?.serialNumber || machineId;
      console.log('📊 PresetValues: Fetching comparison data for', serialno);
      
      Promise.all([
        fetchPresetData(serialno, leftDate, leftShift),
        fetchPresetData(serialno, rightDate, rightShift)
      ])
        .then(([leftResult, rightResult]) => {
          console.log('📊 PresetValues: Got results:', { 
            left: leftResult ? (Array.isArray(leftResult) ? leftResult.length : 'not array') : 'null', 
            right: rightResult ? (Array.isArray(rightResult) ? rightResult.length : 'not array') : 'null' 
          });
          setLeftData(leftResult);
          setRightData(rightResult);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching comparison data:', err);
          setError('Failed to fetch comparison data');
          setLoading(false);
        });
    }
  }, [leftDate, leftShift, rightDate, rightShift, machineId, machineData]);

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Transform data for comparison display
  const getComparisonData = (): ComparisonData[] => {
    // Debug: check all conditions individually
    console.log('📊 PresetValues: getComparisonData check:', {
      leftData: leftData,
      rightData: rightData,
      leftIsArray: Array.isArray(leftData),
      rightIsArray: Array.isArray(rightData),
      leftLength: Array.isArray(leftData) ? leftData.length : 'N/A',
      rightLength: Array.isArray(rightData) ? rightData.length : 'N/A'
    });
    
    // Backend returns flat arrays now
    if (!leftData || !rightData || !Array.isArray(leftData) || !Array.isArray(rightData)) {
      console.log('📊 PresetValues: getComparisonData - RETURNING EMPTY due to invalid data');
      return [];
    }

    console.log('📊 PresetValues: Processing', leftData.length, 'left presets and', rightData.length, 'right presets');

    // Get presets from left side (flat array) - using preset_id as key
    const leftPresets = new Map<number, PresetInfo>();
    leftData.forEach((preset: PresetInfo) => {
      leftPresets.set(preset.preset_id, preset);
    });

    // Get presets from right side (flat array) - using preset_id as key
    const rightPresets = new Map<number, PresetInfo>();
    rightData.forEach((preset: PresetInfo) => {
      rightPresets.set(preset.preset_id, preset);
    });

    // Create comparison data
    const allIds = new Set([...leftPresets.keys(), ...rightPresets.keys()]);
    const comparisonData: ComparisonData[] = [];

    allIds.forEach(id => {
      const leftPreset = leftPresets.get(id);
      const rightPreset = rightPresets.get(id);
      
      comparisonData.push({
        id,
        preset: leftPreset?.preset_name || rightPreset?.preset_name || '',
        leftValue: leftPreset?.current_value ?? '-',
        leftChanges: leftPreset?.number_changes || 0,
        rightValue: rightPreset?.current_value ?? '-',
        rightChanges: rightPreset?.number_changes || 0,
      });
    });

    // Apply difference filter if enabled
    let filteredData = comparisonData;
    if (showOnlyDifferences) {
      filteredData = comparisonData.filter(row => {
        const leftHasValue = row.leftValue !== '-';
        const rightHasValue = row.rightValue !== '-';
        return leftHasValue && rightHasValue && row.leftValue !== row.rightValue;
      });
    }

    // Apply search filter if search term is provided
    if (searchTerm.trim()) {
      filteredData = filteredData.filter(row => {
        const searchLower = searchTerm.toLowerCase().trim();
        return (
          row.preset.toLowerCase().includes(searchLower) ||
          String(row.leftValue).toLowerCase().includes(searchLower) ||
          String(row.rightValue).toLowerCase().includes(searchLower) ||
          row.id.toString().includes(searchLower)
        );
      });
    }

    // Apply sorting
    return filteredData.sort((a, b) => {
      let aValue: any = a[sortField as keyof ComparisonData];
      let bValue: any = b[sortField as keyof ComparisonData];

      // Handle different data types
      if (sortField === 'id' || sortField === 'leftChanges' || sortField === 'rightChanges') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      } else {
        // String comparison
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const tableData = getComparisonData();
  
  // Pagination calculations
  const totalItems = tableData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = tableData.slice(startIndex, endIndex);

  // Reset to first page when data changes or sorting changes
  useEffect(() => {
    setCurrentPage(1);
  }, [tableData.length, sortField, sortDirection, showOnlyDifferences]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Get sort icon for header
  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return (
        <span className="ml-1" style={{ color: 'var(--text-secondary)' }}>
          <svg className="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        </span>
      );
    }
    
    return sortDirection === 'asc' ? (
      <span className="ml-1" style={{ color: 'var(--text-primary)' }}>
        <svg className="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      </span>
    ) : (
      <span className="ml-1" style={{ color: 'var(--text-primary)' }}>
        <svg className="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </span>
    );
  };

  return (
    <div className="rounded-lg border p-3" 
         style={{ 
           backgroundColor: 'var(--bg-primary)', 
           borderColor: 'var(--border-primary)' 
         }}>
      
      {/* Date and Shift Selection */}
      <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} items-center ${isMobile ? 'gap-6' : 'gap-4'} mb-4`}>
        {/* Left Side Selection */}
        <div className={`${isMobile ? 'w-full' : 'flex-1'} ${isMobile ? 'p-5' : 'p-4'} rounded-lg border shadow-sm`} style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
          <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : 'md:grid-cols-2 gap-3'}`}>
            <div className="flex flex-col">
              <label htmlFor="left-date" className={`${isMobile ? 'text-base' : 'text-sm'} font-medium ${isMobile ? 'mb-2' : 'mb-1'}`} style={{ color: 'var(--text-secondary)' }}>Date</label>
              <AvailableDatePicker
                value={leftDate}
                onChange={setLeftDate}
                availableDates={availableDates}
                className={`${isMobile ? 'text-base' : ''}`}
                placeholder="Select date..."
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="left-shift" className={`${isMobile ? 'text-base' : 'text-sm'} font-medium ${isMobile ? 'mb-2' : 'mb-1'}`} style={{ color: 'var(--text-secondary)' }}>Shift</label>
              <select 
                id="left-shift"
                value={leftShift}
                onChange={(e) => setLeftShift(e.target.value)}
                className={`${isMobile ? 'p-3 text-base' : 'p-2'} rounded-md border focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                style={{ 
                  borderColor: 'var(--border-primary)', 
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-primary)',
                  minHeight: isMobile ? '48px' : 'auto'
                }}
              >
                {leftShifts.map(shift => 
                  <option 
                    key={shift} 
                    value={shift}
                    style={{
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    {shift}
                  </option>
                )}
              </select>
            </div>
          </div>
        </div>

        {/* VS Separator */}
        <div className={`flex items-center justify-center ${isMobile ? 'my-4' : 'mx-4'}`}>
          <div className={`bg-blue-500 text-white ${isMobile ? 'px-6 py-3 text-xl' : 'px-4 py-2 text-lg'} rounded-full font-bold shadow-lg transform hover:scale-105 transition-transform`}>
            VS
          </div>
        </div>

        {/* Right Side Selection */}
        <div className={`${isMobile ? 'w-full' : 'flex-1'} ${isMobile ? 'p-5' : 'p-4'} rounded-lg border shadow-sm`} style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
          <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : 'md:grid-cols-2 gap-3'}`}>
            <div className="flex flex-col">
              <label htmlFor="right-date" className={`${isMobile ? 'text-base' : 'text-sm'} font-medium ${isMobile ? 'mb-2' : 'mb-1'}`} style={{ color: 'var(--text-secondary)' }}>Date</label>
              <AvailableDatePicker
                value={rightDate}
                onChange={setRightDate}
                availableDates={availableDates}
                className={`${isMobile ? 'text-base' : ''}`}
                placeholder="Select date..."
              />
            </div>
            <div className="flex flex-col">
              <label htmlFor="right-shift" className={`${isMobile ? 'text-base' : 'text-sm'} font-medium ${isMobile ? 'mb-2' : 'mb-1'}`} style={{ color: 'var(--text-secondary)' }}>Shift</label>
              <select 
                id="right-shift"
                value={rightShift}
                onChange={(e) => setRightShift(e.target.value)}
                className={`${isMobile ? 'p-3 text-base' : 'p-2'} rounded-md border focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                style={{ 
                  borderColor: 'var(--border-primary)', 
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-primary)',
                  minHeight: isMobile ? '48px' : 'auto'
                }}
              >
                {rightShifts.map(shift => 
                  <option 
                    key={shift} 
                    value={shift}
                    style={{
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    {shift}
                  </option>
                )}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className={`${isMobile ? 'space-y-4' : 'flex flex-row items-center gap-4'} mb-4`}>
        <div className={`flex items-center ${isMobile ? 'w-full justify-center bg-gray-50 dark:bg-gray-800 p-3 rounded-lg' : ''}`}>
          <input 
            type="checkbox" 
            id="show-diff" 
            checked={showOnlyDifferences} 
            onChange={(e) => setShowOnlyDifferences(e.target.checked)} 
            className={`${isMobile ? 'h-5 w-5' : 'h-4 w-4'} rounded border-gray-300 text-blue-600 focus:ring-blue-500`}
          />
          <label htmlFor="show-diff" className={`ml-2 ${isMobile ? 'text-base' : 'text-sm'} font-medium`} style={{ color: 'var(--text-secondary)' }}>
            Show only differences
          </label>
        </div>
        <div className={`relative ${isMobile ? 'w-full' : 'flex-grow'}`}>
          <input 
            type="text" 
            placeholder="Search presets..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className={`${isMobile ? 'p-3 pl-12 text-base' : 'p-2 pl-8'} rounded-md border w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
            style={{ 
              borderColor: 'var(--border-primary)', 
              color: 'var(--text-primary)',
              backgroundColor: 'var(--bg-primary)',
              minHeight: isMobile ? '48px' : 'auto'
            }}
          />
          <div className={`absolute ${isMobile ? 'left-4' : 'left-3'} top-1/2 transform -translate-y-1/2`} style={{ color: 'var(--text-secondary)' }}>
            <svg className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Table or Cards View */}
      {!loading && !error && tableData.length > 0 && (
        isMobile ? (
          <div className="space-y-4">
            {paginatedData.map(row => (
              <div key={row.id} className="border rounded-lg p-4 shadow-sm" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)' }}>
                <div className="font-bold text-lg mb-3 pb-2 border-b" style={{ color: 'var(--text-primary)', borderColor: 'var(--border-primary)' }}>
                  <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>#{row.id}</span>
                  <br />
                  {row.preset}
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 pr-2">
                      <div className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                        First Period
                      </div>
                      <div className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>
                        {new Date(leftDate).toLocaleDateString()} / {leftShift}
                      </div>
                      <div className="text-base font-semibold" style={{ color: 'var(--text-accent)' }}>
                        {row.leftValue}
                      </div>
                      <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {row.leftChanges} changes
                      </div>
                    </div>
                    <div className="flex-1 pl-2 border-l" style={{ borderColor: 'var(--border-primary)' }}>
                      <div className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                        Second Period
                      </div>
                      <div className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>
                        {new Date(rightDate).toLocaleDateString()} / {rightShift}
                      </div>
                      <div className={`text-base font-semibold ${row.leftValue !== row.rightValue ? 'bg-red-100 dark:bg-red-900/20 px-2 py-1 rounded' : ''}`} style={{ 
                        color: row.leftValue !== row.rightValue ? (currentTheme.isDark ? '#ef4444' : '#dc2626') : 'var(--text-accent)' 
                      }}>
                        {row.rightValue}
                      </div>
                      <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {row.rightChanges} changes
                      </div>
                    </div>
                  </div>
                  {row.leftValue !== row.rightValue && (
                    <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs text-center" style={{ color: 'var(--text-secondary)' }}>
                      Values differ
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <tr>
                  <th onClick={() => handleSort('id')} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer" style={{ color: 'var(--text-secondary)' }}>Preset No. {getSortIcon('id')}</th>
                  <th onClick={() => handleSort('preset')} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer" style={{ color: 'var(--text-secondary)' }}>Preset {getSortIcon('preset')}</th>
                  <th onClick={() => handleSort('leftValue')} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer" style={{ color: 'var(--text-secondary)' }}>{leftDate} / {leftShift} Value {getSortIcon('leftValue')}</th>
                  <th onClick={() => handleSort('leftChanges')} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer" style={{ color: 'var(--text-secondary)' }}>{leftDate} / {leftShift} Changes {getSortIcon('leftChanges')}</th>
                  <th onClick={() => handleSort('rightValue')} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer" style={{ color: 'var(--text-secondary)' }}>{rightDate} / {rightShift} Value {getSortIcon('rightValue')}</th>
                  <th onClick={() => handleSort('rightChanges')} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer" style={{ color: 'var(--text-secondary)' }}>{rightDate} / {rightShift} Changes {getSortIcon('rightChanges')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" style={{ backgroundColor: 'var(--bg-primary)' }}>
                {paginatedData.map(row => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>{row.id}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>{row.preset}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono" style={{ color: 'var(--text-primary)' }}>{row.leftValue}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>{row.leftChanges}</td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm font-mono ${row.leftValue !== row.rightValue ? 'font-bold' : ''}`} style={{ color: row.leftValue !== row.rightValue ? (currentTheme.isDark ? '#ef4444' : '#dc2626') : 'var(--text-primary)' }}>{row.rightValue}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>{row.rightChanges}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {loading && (
        <div className="text-center p-8" style={{ color: 'var(--text-primary)' }}>Loading preset data...</div>
      )}

      {error && (
        <div className="text-center p-8" style={{ color: currentTheme.isDark ? '#ef4444' : '#dc2626' }}>Error: {error}</div>
      )}

      {!loading && !error && (!leftDate || !leftShift || !rightDate || !rightShift) && (
        <div className="text-center p-8" style={{ color: 'var(--text-secondary)' }}>Please select dates and shifts to compare.</div>
      )}

      {!loading && !error && tableData.length === 0 && leftDate && leftShift && rightDate && rightShift && (
        <div className="text-center p-8" style={{ color: 'var(--text-secondary)' }}>No preset data found for the selected criteria or filters.</div>
      )}

      {/* Pagination Controls */}
      {!loading && !error && tableData.length > 0 && paginatedData.length > 0 && (
        <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} items-center justify-between mt-6 ${isMobile ? 'space-y-3' : ''}`}>
          <div className={`${isMobile ? 'text-base' : 'text-sm'} ${isMobile ? 'text-center' : 'mb-2 md:mb-0'}`} style={{ color: 'var(--text-secondary)' }}>
            Showing {startIndex + 1}-{endIndex > totalItems ? totalItems : endIndex} of {totalItems} presets
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1} 
              className={`${isMobile ? 'px-4 py-2 text-base' : 'px-3 py-1'} border rounded-md disabled:opacity-50 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700`}
              style={{ 
                borderColor: 'var(--border-primary)', 
                color: 'var(--text-primary)',
                backgroundColor: 'var(--bg-secondary)',
                minHeight: isMobile ? '44px' : 'auto'
              }}
            >
              Previous
            </button>
            <span className={`${isMobile ? 'px-4 text-base' : 'px-3'}`} style={{ color: 'var(--text-primary)' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages} 
              className={`${isMobile ? 'px-4 py-2 text-base' : 'px-3 py-1'} border rounded-md disabled:opacity-50 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700`}
              style={{ 
                borderColor: 'var(--border-primary)', 
                color: 'var(--text-primary)',
                backgroundColor: 'var(--bg-secondary)',
                minHeight: isMobile ? '44px' : 'auto'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresetValues;
